import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, NgModule, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule, SelectionType } from '@siemens/ngx-datatable';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProgressbarModule } from 'ngx-bootstrap/progressbar';
import { filter, take } from 'rxjs/operators';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { RepoClientsService } from '../../../data/repositories/repo-clients.service';
import { RepoImportService } from '../../../data/repositories/repo-import.service';
import { RepoStorageService, StorageContainer, StorageUploadFile } from '../../../data/repositories/repo-storage.service';

@Component({
    selector: 'app-import-clients',
    templateUrl: './import-clients.component.html',
    styleUrls: ['./import-clients.component.scss'],
    standalone: false,
})
export class ImportClientsComponent implements OnInit {
    @Output() action = new EventEmitter();

    currentStep = 1;
    importFile: any;
    // @ts-ignore TS2564
    destinationColumns: any[];
    maxImportFileSize = 10000000;
    // @ts-ignore TS2564
    fileError: string;
    // @ts-ignore TS2564
    mappingError: string;
    // @ts-ignore TS2564
    fileUrl: string;
    firstRowIsColumnNames = true;
    // @ts-ignore TS2564
    sourceColumns: any[];
    itemRows = [];
    selected = [];
    importItems = [];
    importCount = 0;
    importTotal = 0;
    importProgress = 0;
    selectionType = SelectionType;

    constructor(
        private bsModalRef: BsModalRef,
        private storage: RepoStorageService,
        private repoCustomers: RepoClientsService,
        private logger: BxLogger,
        private importRepo: RepoImportService
    ) {}

    ngOnInit(): void {
        this.currentStep = 1;
        this.loadImportFields();
    }

    loadImportFields() {
        this.importRepo.GetImportFields('customers').then((result) => {
            this.destinationColumns = result.data.map((e: any, i: number) => {
                e['index'] = i;
                return e;
            });

            this.destinationColumns.unshift({
                name: '(Ignore)',
                description: 'Ignore this column',
                index: 9999,
                ignore: true,
            });
        });
    }

    fileBrowseHandler(files: FileList | null) {
        if (files?.length) {
            const file = files[0] as File;
            if (file.size > this.maxImportFileSize) {
                this.fileError = `File size is too large. Maximum accepted size is ${(this.maxImportFileSize / (1024 * 1024)).toFixed(
                    2
                )}MB`;
                return;
            }
            this.importFile = files[0];
            this.storage
                .upload([file], { container: StorageContainer.temp })
                .pipe(
                    filter((e) => e.type === HttpEventType.Response),
                    take(1)
                )
                .subscribe((event) => {
                    if (event.type == HttpEventType.Response) {
                        var file = (event.body as StorageUploadFile[])[0];
                        if (file) {
                            this.fileUrl = file.fileUrl;
                            this.importRepo
                                .GetFieldsInFile(this.fileUrl, this.firstRowIsColumnNames)
                                .then(this.readExcelFileSuccess.bind(this), this.readExcelFileError.bind(this));
                        }
                    }
                });
        }
    }

    // @ts-ignore TS7006
    readExcelFileSuccess(result) {
        this.sourceColumns = result.data;
        this.prefillDestination();
        this.moveNext();
    }

    // @ts-ignore TS7006
    readExcelFileError(error) {
        this.fileError =
            'Something seems to be wrong with the format of your file. We recommend opening the file in Excel, and saving as a new spreadsheet.';
    }

    prefillDestination() {
        for (let i = 0; i < this.destinationColumns.length; i++) {
            const destinationColumn = this.destinationColumns[i];
            for (let x = 0; x < this.sourceColumns.length; x++) {
                const sourceColumn = this.sourceColumns[x];
                if (sourceColumn.name.toLowerCase() === destinationColumn.name.toLowerCase()) {
                    sourceColumn.destination = destinationColumn;
                }
            }
        }
    }

    destinationChanged() {
        this.validateMappings();
    }

    validateMappings() {
        const inUse: string[] = [];
        // @ts-ignore TS2322
        this.mappingError = undefined;
        for (let i = 0; i < this.sourceColumns.length; i++) {
            const sourceColumn = this.sourceColumns[i];
            sourceColumn.error = undefined;
            if (sourceColumn.destination !== undefined && !sourceColumn.destination.ignore) {
                if (inUse.indexOf(sourceColumn.destination.name) !== -1) {
                    sourceColumn.error = `'${sourceColumn.destination.name}' has already been selected.`;
                    this.mappingError = 'One or more destination columns have been selected more than once.';
                }
                inUse.push(sourceColumn.destination.name);
            }
        }
    }

    previewMapping() {
        const mappings = [] as any[];
        this.sourceColumns.forEach((sourceColumn) => {
            if (sourceColumn.destination && !sourceColumn.destination.ignore) {
                mappings.push({
                    Source: sourceColumn.name,
                    Destination: sourceColumn.destination.name,
                });
            }
        });

        this.importRepo.GetImportPreview('customers', mappings, this.fileUrl, this.firstRowIsColumnNames).then((result) => {
            this.itemRows = result.data.Customers;
            this.itemRows.forEach((row) => {
                let contactCount = 0;
                // @ts-ignore TS2339
                if (row.Contact1FirstName) contactCount++;
                // @ts-ignore TS2339
                if (row.Contact2FirstName) contactCount++;
                // @ts-ignore TS2339
                if (row.Contact3FirstName) contactCount++;
                // @ts-ignore TS2339
                if (contactCount) row.contactCount = contactCount;
            });
            this.onSelect({ selected: this.itemRows }); // select all
            this.moveNext();
        });
    }

    isDestinationMapped(name: string) {
        return this.sourceColumns.some((sc) => {
            return sc.destination?.name === name;
        });
    }

    // @ts-ignore TS7031
    onSelect({ selected }) {
        this.selected.splice(0, this.selected.length);
        // @ts-ignore TS2345
        this.selected.push(...selected);
    }

    runImport() {
        this.moveNext();
        this.importProgress = 0;
        this.importCount = 0;
        this.importTotal = this.selected.length;
        this.importItems = [];
        this.importNextChunk();
    }

    importNextChunk() {
        for (let i = this.importCount; i < this.selected.length; i++) {
            const item = this.selected[i];
            // @ts-ignore TS2345
            this.importItems.push(item);
            this.importCount++;
            this.importProgress = Math.ceil((this.importCount / this.importTotal) * 100);
            // POST 100 at a time
            if (this.importItems.length === 100) break;
        }
        if (this.importItems.length) {
            this.postImportData();
        } else {
            this.onSuccess();
        }
    }

    postImportData() {
        return this.repoCustomers.Import(this.importItems).then(() => {
            this.importItems.length = 0;
            this.importNextChunk();
        });
    }

    onSuccess() {
        this.logger.success(this.importTotal.toString() + ' clients imported successfully.');
        this.bsModalRef.hide();
        this.action.emit({ cancelled: false });
    }

    moveNext() {
        this.currentStep++;
    }

    cancel() {
        this.bsModalRef.hide();
        this.action.emit({ cancelled: true });
    }
    // @ts-ignore TS7006
    goToStep(i) {
        this.currentStep = i;
    }
}

@NgModule({
    declarations: [ImportClientsComponent],
    imports: [CommonModule, NgxDatatableModule, ProgressbarModule, FormsModule, NgSelectModule],
    exports: [ImportClientsComponent],
})
export class ImportClientsModule {}
